import axios from 'axios';
import appconst from './appConstants';
import cookieUtils from './cookieUtils';
import { AppModule } from '@/store/modules/app'
import Errors from '@/models/Shared/Errors'
import ErrorMessage from '@/models/Shared/ErrorMessage'
import { getAuth, signOut } from "firebase/auth";
import IdentityProvider from '@/models/Users/IdentityProvider';

// SSO re-validation interval: 8 hours in milliseconds
// This is a proactive frontend check — the backend enforces this independently
const SSO_REVALIDATION_INTERVAL_MS = 8 * 60 * 60 * 1000;

// Mutex to prevent concurrent SSO refresh attempts
let ssoRefreshInProgress: Promise<boolean> | null = null;

/**
 * Attempts to refresh the SSO session. Returns true on success.
 * Uses a mutex to prevent concurrent refresh attempts.
 */
async function attemptSSORefresh(): Promise<boolean> {
  if (ssoRefreshInProgress) {
    return ssoRefreshInProgress;
  }

  ssoRefreshInProgress = AppModule.refreshSSOSession().finally(() => {
    ssoRefreshInProgress = null;
  });

  return ssoRefreshInProgress;
}

/**
 * Forces logout and redirects to login page.
 */
async function forceLogout() {
  console.log('[SSO] Forcing logout.');
  const auth = getAuth();
  await signOut(auth);
  AppModule.cleanState();
  window.location.href = '/login';
}

/**
 * Proactive frontend check: if the SSO session is approaching expiry,
 * trigger a refresh before the backend rejects the request.
 * This provides a smoother UX than waiting for a 401.
 */
async function ensureSSOSessionValid(): Promise<boolean> {
  const profile = AppModule.userProfile;
  if (!profile || profile.identityProvider !== IdentityProvider.Microsoft) {
    return true;
  }

  const lastValidatedStr = localStorage.getItem('sso.lastValidatedAt');
  if (!lastValidatedStr) {
    // Pre-existing session from before this feature — backend will enforce
    return true;
  }

  const elapsed = Date.now() - new Date(lastValidatedStr).getTime();
  if (elapsed < SSO_REVALIDATION_INTERVAL_MS) {
    return true;
  }

  console.log('[SSO] Proactive re-validation needed. Last validated:', lastValidatedStr);
  const success = await attemptSSORefresh();
  if (!success) {
    await forceLogout();
    return false;
  }
  return true;
}

const ajax = axios.create({
  baseURL: appconst.remoteServiceBaseUrl,
  timeout: 30000,
});

// Request interceptor: proactive SSO check + auth headers
ajax.interceptors.request.use(async (config)=> {
  if (!config.url.includes('https://storage.googleapis.com')) {
    if (config.url.includes('v1/sys/anon') || config.url.includes('v1/anon') || config.url.includes('v1/public/')) {
      // Public/anon endpoints - no SSO check needed
      if (cookieUtils.getToken()) {
        config.headers.common.Authorization = `Bearer ${cookieUtils.getToken()}`;
      }
    } else {
      if (AppModule.currentUser) {
        // Proactive SSO re-validation (smoother UX than waiting for 401)
        const ssoValid = await ensureSSOSessionValid();
        if (!ssoValid) {
          return Promise.reject(new axios.Cancel('SSO session expired'));
        }

        const idToken = await AppModule.currentUser.getIdToken();
        config.headers.common.Authorization = "Bearer " + idToken;
        if (AppModule.currentAgencyContext) {
          config.headers.common["AgencyId"] = AppModule.currentAgencyContext.id;
        }
      }
    }
  }
  return config;
}, error => Promise.reject(error));

// Response interceptor: handle backend-enforced SSO session expiry
// This is the safety net — if the backend rejects with SSO_SESSION_EXPIRED,
// attempt a refresh and retry the request once.
ajax.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle SSO_SESSION_EXPIRED from the backend, and only retry once
    if (error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.Code === 'SSO_SESSION_EXPIRED' &&
        !originalRequest._ssoRetried) {

      console.log('[SSO] Backend rejected request: SSO session expired. Attempting refresh...');
      originalRequest._ssoRetried = true;

      const success = await attemptSSORefresh();
      if (success && AppModule.currentUser) {
        // Refresh succeeded — retry the original request with a new token
        const idToken = await AppModule.currentUser.getIdToken();
        originalRequest.headers.Authorization = "Bearer " + idToken;
        return ajax(originalRequest);
      }

      // Refresh failed — force logout
      await forceLogout();
    }

    return Promise.reject(error);
  }
);

export default ajax;
